import React, { useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import LazyLoad from 'react-lazyload';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle, faTimes } from '@fortawesome/pro-light-svg-icons';
import { faPencilAlt, faLink, faTag } from '@fortawesome/pro-solid-svg-icons';

import Loader from '../../Loader/Loader';
import { getSmartImage, dismissEvent } from '../../../Helpers/helpers';
import { getPrettyPriceDisplay } from '../../../Helpers/formatting';
import { getAffiliateLink } from '../../../Helpers/attribution_helpers';

const CollectionProductClassic = props => {
  const {
    user,
    collection,
    pin,
    code,
    copyCode,
    lastLocation,
    canEdit,
    DragHandle,
    getActionsPanel,
    showingActionsPanel,
    navigateToProduct,
    updateLinkRefAttributes,
    copyLink,
    toggleEditMode,
    isGallery
  } = props;
  const { title, description, image, isHidden } = pin;

  const [viewingDescription, setViewingDescription] = useState(false);

  const hasDescription = description && description.length > 0;
  const affiliateLink = getAffiliateLink(pin, user, collection.user, collection);

  return (
    <div className={cn('collection-product', collection.skinType, { hidden: isHidden, 'hide-under-sm': props.isDesktopOnly })}>
      <a
        ref={ref => ref && updateLinkRefAttributes(ref)}
        href={affiliateLink}
        target='_blank'
        rel='nofollow noopener noreferrer'
        className={cn('collection-product-img-wrapper', {
          smooth: !canEdit && !!lastLocation,
          'is-gallery': isGallery
        })}
      >
        {!!isHidden && (
          <div className='hidden-cover'>
            <div className='badge'>HIDDEN</div>
          </div>
        )}
        <LazyLoad height={200} offset={window.__IS_SNAP__ ? 2000 : 480}>
          <img
            alt={title || 'Product'}
            onClick={navigateToProduct}
            className={cn('product-image', {
              empty: !image,
              'is-gallery': props.isGallery
            })}
            src={getSmartImage(image)}
          />
        </LazyLoad>
      </a>
      {canEdit && (
        <div className='action-btn-container'>
          {!showingActionsPanel && <FontAwesomeIcon className='hide-on-mobile' onClick={copyLink} icon={faLink} />}
          {!showingActionsPanel && <FontAwesomeIcon className='hide-on-mobile' onClick={toggleEditMode} icon={faPencilAlt} />}
          {getActionsPanel()}
        </div>
      )}
      {!props.isGallery && (
        <div className='meta-container'>
          <a
            ref={ref => ref && updateLinkRefAttributes(ref)}
            href={affiliateLink}
            target='_blank'
            className='header-container'
            rel='nofollow noopener noreferrer'
            onClick={navigateToProduct}
          >
            <h3 className='header'>{title}</h3>
            {code && !!pin.allow_displaying_codes && (
              <div onClick={dismissEvent} className='code-container'>
                <FontAwesomeIcon onClick={copyCode} icon={faTag} />
                <div onClick={copyCode} className='code'>
                  USE CODE <span className='display'>{code.displayText}</span> FOR{' '}
                  {code.isPercentage ? `${code.rate}% OFF` : `${getPrettyPriceDisplay(code.rate)} OFF`}
                </div>
              </div>
            )}
          </a>
          <a
            ref={ref => ref && updateLinkRefAttributes(ref)}
            className='link-to-product-btn'
            href={affiliateLink}
            target='_blank'
            rel='nofollow noopener noreferrer'
            onClick={navigateToProduct}
          >
            SHOP NOW
          </a>
        </div>
      )}
      {(props.isDeleting || props.isBeingEdited || props.isDuplicating) && (
        <div className='adjusting-product-loader active'>
          <Loader size={100} />
        </div>
      )}
      {!viewingDescription && (
        <div className={cn('show-description-icn', { active: hasDescription })}>
          <FontAwesomeIcon
            onClick={e => {
              e.stopPropagation();
              hasDescription && setViewingDescription(true);
            }}
            icon={faInfoCircle}
          />
        </div>
      )}
      {canEdit && <DragHandle />}
      <div
        className={cn('product-description-overlay', {
          visible: viewingDescription,
          long: description && description.length > 240
        })}
      >
        {description}
        <FontAwesomeIcon className='close-icon' icon={faTimes} onClick={() => setViewingDescription(false)} />
      </div>
    </div>
  );
};

CollectionProductClassic.propTypes = {
  // Main Data
  user: PropTypes.object.isRequired,
  collection: PropTypes.object.isRequired,
  pin: PropTypes.object.isRequired,
  lastLocation: PropTypes.object,

  // Variables
  index: PropTypes.number.isRequired,
  canEdit: PropTypes.bool.isRequired,
  isDeleting: PropTypes.bool,
  isDuplicating: PropTypes.bool,
  isBeingEdited: PropTypes.bool,
  showingActionsPanel: PropTypes.bool,

  // Functions
  navigateToProduct: PropTypes.func.isRequired,
  updateLinkRefAttributes: PropTypes.func.isRequired,
  copyLink: PropTypes.func.isRequired,
  toggleEditMode: PropTypes.func.isRequired,

  // UI
  isGallery: PropTypes.bool,

  // External Divs
  getActionsPanel: PropTypes.func,
  DragHandle: PropTypes.func
};

export default CollectionProductClassic;
